/**
 * 서비스 관리 Container 컴포넌트
 * 상태 관리 및 비즈니스 로직을 담당합니다.
 */
import React, { useCallback, useState } from 'react';
import { useViewMode } from '@/hooks/common';
import { MSAService } from '@/types';
import ApproveServiceDialog from '@/components/services/management/superadmin/dialogs/ApproveServiceDialog';
import ServiceClientDialog from '@/components/services/management/superadmin/dialogs/ServiceClientDialog';
import ServiceRegisterDialog from '@/components/services/registration/ServiceRegisterDialog';
import ConfirmDialog from '@/components/users/management/common/dialogs/ConfirmDialog';
import { ServiceDetailDialog } from '@/components/common/shared/ServiceDetailDialog';
import CreditPlanManagement from '@/components/services/management/superadmin/CreditPlanManagement';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Trash2 } from 'lucide-react';
import { useServiceManagement } from './hooks/useServiceManagement';
import { useServiceFilters } from './hooks/useServiceFilters';
import { useServiceDialogs } from './hooks/useServiceDialogs';
import { ServiceManagementView } from './components/ServiceManagementView';

const ServiceManagement: React.FC = () => {
  const {
    activeTab,
    selectedServiceId,
    selectedService,
    showServiceDetail,
    showQuotaPlanManagement,
    serviceCounts,
    services,
    loading,
    error,
    healthStatuses,
    success,
    setSelectedServiceId,
    setSelectedService,
    setShowServiceDetail,
    setShowQuotaPlanManagement,
    setError,
    setSuccess,
    handleSubTabChange,
    handleRefresh,
    handleServiceClick,
    handleManagePlans,
    fetchServices,
    fetchServiceCounts,
    serviceActions,
  } = useServiceManagement();

  const { view, setView } = useViewMode('serviceManagement_viewMode', 'card');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  // 로그인 연동 다이얼로그를 띄울 서비스 (#134)
  const [clientDialogService, setClientDialogService] = useState<
    { slug: string; name: string } | null
  >(null);
  // 서비스 등록 다이얼로그 (#136)
  const [registerOpen, setRegisterOpen] = useState(false);

  const {
    searchText,
    filterCategory,
    filterVisibility,
    filterStatus,
    filteredServices,
    setSearchText,
    setFilterCategory,
    setFilterVisibility,
    setFilterStatus,
    resetFilters,
    getFilteredServices,
  } = useServiceFilters({ services, activeTab });

  const {
    approveModalOpen,
    showClearAllDialog,
    confirmModal,
    setApproveModalOpen,
    setShowClearAllDialog,
    setConfirmModal,
    openConfirmModal,
    handleClearAll,
  } = useServiceDialogs(
    setSuccess,
    setError,
    async () => {
      await fetchServiceCounts();
      await fetchServices();
    }
  );

  const handleApproveClick = useCallback((serviceId: number) => {
    setSelectedServiceId(serviceId);
    setApproveModalOpen(true);
  }, [setSelectedServiceId, setApproveModalOpen]);

  const handleApprove = useCallback(async (serviceId: number, isPublic: boolean) => {
    try {
      setError('');
      setSuccess('');
      const message = await serviceActions.approveService(serviceId, isPublic);
      setApproveModalOpen(false);
      setSelectedServiceId(null);
      setSuccess(message);
      await fetchServiceCounts();
    } catch (error) {
      // Error is handled by hook
    }
  }, [serviceActions, setError, setSuccess, setApproveModalOpen, setSelectedServiceId, fetchServiceCounts]);

  const handleBulkApprove = useCallback(async () => {
    if (selectedIds.size === 0) return;
    try {
      setError('');
      setSuccess('');
      const message = await serviceActions.bulkApproveServices(Array.from(selectedIds), true);
      setSelectedIds(new Set());
      setSuccess(message);
      await fetchServiceCounts();
    } catch (error) {
      // Error is handled by hook
    }
  }, [selectedIds, serviceActions, setError, setSuccess, fetchServiceCounts]);

  const handleBulkSuspend = useCallback(async () => {
    if (selectedIds.size === 0) return;
    try {
      setError(''); setSuccess('');
      const message = await serviceActions.bulkSuspendServices(Array.from(selectedIds));
      setSelectedIds(new Set());
      setSuccess(message);
      await fetchServiceCounts();
    } catch (error) { /* handled by hook */ }
  }, [selectedIds, serviceActions, setError, setSuccess, fetchServiceCounts]);

  const handleBulkResume = useCallback(async () => {
    if (selectedIds.size === 0) return;
    try {
      setError(''); setSuccess('');
      const message = await serviceActions.bulkResumeServices(Array.from(selectedIds));
      setSelectedIds(new Set());
      setSuccess(message);
      await fetchServiceCounts();
    } catch (error) { /* handled by hook */ }
  }, [selectedIds, serviceActions, setError, setSuccess, fetchServiceCounts]);

  const handleBulkDelete = useCallback(async () => {
    if (selectedIds.size === 0) return;
    try {
      setError(''); setSuccess('');
      const message = await serviceActions.bulkDeleteServices(Array.from(selectedIds));
      setSelectedIds(new Set());
      setSuccess(message);
      await fetchServiceCounts();
    } catch (error) { /* handled by hook */ }
  }, [selectedIds, serviceActions, setError, setSuccess, fetchServiceCounts]);

  const handleBulkSetPublic = useCallback(async () => {
    if (selectedIds.size === 0) return;
    try {
      setError(''); setSuccess('');
      const message = await serviceActions.bulkSetVisibility(Array.from(selectedIds), true);
      setSelectedIds(new Set());
      setSuccess(message);
      await fetchServiceCounts();
    } catch (error) { /* handled by hook */ }
  }, [selectedIds, serviceActions, setError, setSuccess, fetchServiceCounts]);

  const handleBulkSetPrivate = useCallback(async () => {
    if (selectedIds.size === 0) return;
    try {
      setError(''); setSuccess('');
      const message = await serviceActions.bulkSetVisibility(Array.from(selectedIds), false);
      setSelectedIds(new Set());
      setSuccess(message);
      await fetchServiceCounts();
    } catch (error) { /* handled by hook */ }
  }, [selectedIds, serviceActions, setError, setSuccess, fetchServiceCounts]);

  const handleConfirmAction = useCallback(async () => {
    if (!confirmModal.serviceId) return;

    try {
      setError('');
      setSuccess('');

      let message = '';
      switch (confirmModal.type) {
        case 'suspend':
          message = await serviceActions.suspendService(confirmModal.serviceId);
          break;
        case 'resume':
          message = await serviceActions.resumeService(confirmModal.serviceId);
          break;
        case 'delete':
          message = await serviceActions.deleteService(confirmModal.serviceId);
          break;
        case 'reject':
          message = await serviceActions.rejectService(confirmModal.serviceId);
          break;
        case 'restore':
          message = await serviceActions.restoreService(confirmModal.serviceId);
          break;
        case 'cancel_rejection':
          message = await serviceActions.cancelRejection(confirmModal.serviceId);
          break;
        case 'visibility':
          message = await serviceActions.updateServiceVisibility(confirmModal.serviceId, confirmModal.newVisibility!);
          break;
      }

      setSuccess(message);
      await fetchServiceCounts();
    } catch (error) {
      // Error is handled by hook
    } finally {
      setConfirmModal(prev => ({ ...prev, open: false }));
    }
  }, [confirmModal, serviceActions, setError, setSuccess, setConfirmModal, fetchServiceCounts]);

  const handleClearAllClick = useCallback(() => {
    const filteredServices = getFilteredServices('deleted_rejected');
    if (filteredServices.length === 0) {
      setError('클리어할 서비스가 없습니다.');
      return;
    }
    setShowClearAllDialog(true);
  }, [getFilteredServices, setError, setShowClearAllDialog]);

  const handleClearAllConfirm = useCallback(async () => {
    const filteredServices = getFilteredServices('deleted_rejected');
    await handleClearAll(filteredServices);
  }, [getFilteredServices, handleClearAll]);

  const getConfirmModalIcon = () => {
    if (confirmModal.type === 'delete' || confirmModal.type === 'reject') {
      return <span className="text-red-500 text-2xl">⚠️</span>;
    } else if (confirmModal.type === 'suspend') {
      return <span className="text-orange-500 text-2xl">⏸️</span>;
    } else if (confirmModal.type === 'resume') {
      return <span className="text-green-500 text-2xl">▶️</span>;
    } else if (confirmModal.type === 'restore' || confirmModal.type === 'cancel_rejection') {
      return <span className="text-blue-500 text-2xl">↩️</span>;
    } else if (confirmModal.type === 'visibility') {
      return <span className="text-2xl">🌐</span>;
    }
    return null;
  };

  const getClearAllDescription = () => {
    const filteredServices = getFilteredServices('deleted_rejected');
    const deletedCount = filteredServices.filter(s => s.status === 'deleted').length;
    const rejectedCount = filteredServices.filter(s => s.status === 'rejected').length;
    return `삭제/거부됨 탭의 모든 서비스(${filteredServices.length}개)를 DB에서 완전히 삭제하시겠습니까?\n\n` +
      `- 삭제됨: ${deletedCount}개\n` +
      `- 거부됨: ${rejectedCount}개\n\n` +
      `⚠️ 완전 삭제 시 서비스가 DB에서 물리적으로 제거되며, MSA 서비스가 다시 등록할 수 있습니다.`;
  };

  return (
    <>
      <ServiceManagementView
        activeTab={activeTab}
        view={view}
        loading={loading || serviceActions.loading}
        error={error || serviceActions.error || ''}
        success={success}
        serviceCounts={serviceCounts}
        filteredServices={filteredServices}
        healthStatuses={healthStatuses}
        searchText={searchText}
        filterCategory={filterCategory}
        filterVisibility={filterVisibility}
        filterStatus={filterStatus}
        onTabChange={(tab) => { handleSubTabChange(tab); setSelectedIds(new Set()); }}
        onViewChange={setView}
        onRefresh={handleRefresh}
        onRegisterClick={() => setRegisterOpen(true)}
        onSearchTextChange={setSearchText}
        onFilterCategoryChange={setFilterCategory}
        onFilterVisibilityChange={setFilterVisibility}
        onFilterStatusChange={setFilterStatus}
        onResetFilters={resetFilters}
        onClearAllClick={handleClearAllClick}
        onErrorClose={() => {
          setError('');
          serviceActions.setError('');
        }}
        onSuccessClose={() => setSuccess('')}
        onApproveClick={handleApproveClick}
        onReject={(id: number, name: string) => openConfirmModal('reject', id, name)}
        onSuspend={(id: number, name: string) => openConfirmModal('suspend', id, name)}
        onResume={(id: number, name: string) => openConfirmModal('resume', id, name)}
        onDelete={(id: number, name: string) => openConfirmModal('delete', id, name)}
        onRestore={(id: number, name: string) => openConfirmModal('restore', id, name)}
        onCancelRejection={(id: number, name: string) => openConfirmModal('cancel_rejection', id, name)}
        onToggleVisibility={(id: number, name: string, newVisibility: boolean) => openConfirmModal('visibility', id, name, newVisibility)}
        onServiceClick={handleServiceClick}
        onManagePlans={handleManagePlans}
        onManageClient={(service: MSAService) => setClientDialogService({
          slug: service.service_slug,
          name: service.service_name,
        })}
        getFilteredServices={getFilteredServices}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        onBulkApprove={handleBulkApprove}
        onBulkSuspend={handleBulkSuspend}
        onBulkResume={handleBulkResume}
        onBulkDelete={handleBulkDelete}
        onBulkSetPublic={handleBulkSetPublic}
        onBulkSetPrivate={handleBulkSetPrivate}
      />

      {/* 관리자가 서비스를 직접 등록한다 (#136) */}
      <ServiceRegisterDialog
        open={registerOpen}
        role="admin"
        onClose={() => setRegisterOpen(false)}
        onRegistered={async () => {
          await fetchServiceCounts();
          await fetchServices();
        }}
      />

      {/* 로그인 연동(OIDC 클라이언트) 발급 · 관리 (#134) */}
      <ServiceClientDialog
        open={clientDialogService !== null}
        serviceSlug={clientDialogService?.slug ?? null}
        serviceName={clientDialogService?.name}
        onClose={() => setClientDialogService(null)}
      />

      <ApproveServiceDialog
        open={approveModalOpen}
        serviceId={selectedServiceId}
        onClose={() => {
          setApproveModalOpen(false);
          setSelectedServiceId(null);
        }}
        onApprove={handleApprove}
      />

      <ConfirmDialog
        open={confirmModal.open}
        title={confirmModal.title}
        description={confirmModal.message}
        confirmText="확인"
        cancelText="취소"
        variant={confirmModal.type === 'delete' || confirmModal.type === 'reject' ? 'destructive' : 'default'}
        icon={getConfirmModalIcon()}
        onConfirm={handleConfirmAction}
        onCancel={() => setConfirmModal(prev => ({ ...prev, open: false }))}
      />

      {selectedService && (
        <>
          <ServiceDetailDialog
            service={selectedService}
            isOpen={showServiceDetail}
            onClose={() => {
              setShowServiceDetail(false);
              setSelectedService(null);
            }}
            healthStatuses={healthStatuses}
            isSubscribed={false}
          />

          {showQuotaPlanManagement && selectedService && (
            <Dialog open={showQuotaPlanManagement} onOpenChange={setShowQuotaPlanManagement}>
              <DialogContent 
                className="max-w-4xl max-h-[90vh] overflow-y-auto"
                onClose={() => {
                  setShowQuotaPlanManagement(false);
                  setSelectedService(null);
                }}
              >
                <DialogHeader>
                  <DialogTitle>서비스 요금제 관리 - {selectedService.service_name}</DialogTitle>
                </DialogHeader>
                <CreditPlanManagement
                  serviceSlug={selectedService.service_slug}
                  serviceName={selectedService.service_name}
                />
                <DialogFooter className="pt-4 border-t mt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowQuotaPlanManagement(false);
                      setSelectedService(null);
                    }}
                  >
                    닫기
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </>
      )}
      
      <ConfirmDialog
        open={showClearAllDialog}
        title="모두 클리어 확인"
        description={getClearAllDescription()}
        confirmText="완전 삭제"
        cancelText="취소"
        variant="destructive"
        icon={<Trash2 className="h-5 w-5" />}
        onConfirm={handleClearAllConfirm}
        onCancel={() => setShowClearAllDialog(false)}
      />
    </>
  );
};

export default ServiceManagement;
